import type { VesselKind } from '../entities/Tug.ts'

type Layer = 'diesel' | 'outboard' | 'water'
interface Voice { source: AudioBufferSourceNode; gain: GainNode; filter: BiquadFilterNode }
interface Profile { engine: Exclude<Layer,'water'>; idleRate: number; fullRate: number; volume: number; cutoff: number; wash: number }
export const BOAT_AUDIO_PROFILES: Record<VesselKind, Profile> = {
  zodiac: { engine:'outboard', idleRate:.95, fullRate:1.6, volume:.65, cutoff:3900, wash:.8 },
  tug: { engine:'diesel', idleRate:.8, fullRate:1.18, volume:.75, cutoff:1900, wash:.6 },
  icebreaker: { engine:'diesel', idleRate:.63, fullRate:.91, volume:.85, cutoff:1400, wash:.7 },
  'cruise-ship': { engine:'diesel', idleRate:.56, fullRate:.8, volume:.65, cutoff:1000, wash:.65 },
  'container-ship': { engine:'diesel', idleRate:.5, fullRate:.76, volume:.7, cutoff:950, wash:.7 },
  'aircraft-carrier': { engine:'diesel', idleRate:.58, fullRate:.86, volume:.7, cutoff:1150, wash:.75 },
  'police-boat': { engine:'outboard', idleRate:.8, fullRate:1.35, volume:.75, cutoff:3500, wash:.8 },
  fireboat: { engine:'diesel', idleRate:.92, fullRate:1.3, volume:.8, cutoff:2500, wash:.75 },
  'landing-craft': { engine:'diesel', idleRate:.88, fullRate:1.22, volume:.8, cutoff:2300, wash:.75 },
  sailboat: { engine:'outboard', idleRate:.65, fullRate:.95, volume:.22, cutoff:1300, wash:.8 },
}

const unit = (n: number) => Number.isFinite(n) ? Math.min(1, Math.max(0,n)) : 0
export function boatAudioMix(kind: VesselKind, throttle: number, load: number, speed: number) {
  const p=BOAT_AUDIO_PROFILES[kind], demand=unit(Math.abs(throttle)), working=unit(load)
  const wash=unit(Math.abs(speed)/14)
  return { engine:p.engine, rate:p.idleRate+(p.fullRate-p.idleRate)*demand,
    engineGain:p.volume*(.34+demand*.5+working*.16)*(kind==='sailboat' ? .15+demand*.85 : 1),
    cutoff:p.cutoff*(.55+demand*.45), waterGain:p.wash*(.12+wash*.58), waterRate:.85+wash*.3 }
}

/** Three short loops, decoded once. No per-frame synthesis or AudioWorklet. */
export class BoatAudio {
  readonly ready: Promise<void>
  private readonly voices = new Map<Layer,Voice>()
  private readonly retiring = new Set<Voice>()
  private kind: VesselKind = 'tug'
  private state: [number,number,number] = [0,0,0]
  private disposed = false

  constructor(private readonly context: BaseAudioContext, private readonly destination: AudioNode) {
    for(const name of ['diesel','outboard','water'] as const) this.install(name,this.backup(name),false)
    this.ready=Promise.all((['diesel','outboard','water'] as const).map(async name=>{
      try {
        const response=await fetch(`/audio/boats/${name}.wav`)
        if(!response.ok) throw new Error(`HTTP ${response.status}`)
        const buffer=await context.decodeAudioData(await response.arrayBuffer())
        if(!this.disposed) this.install(name,buffer,true)
      } catch(error) { console.warn(`Boat ${name} recording unavailable; using quiet backup`,error) }
    })).then(()=>undefined)
  }

  setVessel(kind: VesselKind): void { this.kind=kind; this.update(...this.state) }

  private install(name: Layer, buffer: AudioBuffer, fade: boolean): void {
    const ctx=this.context, now=ctx.currentTime, old=this.voices.get(name)
    const source=ctx.createBufferSource(),gain=ctx.createGain(),filter=ctx.createBiquadFilter()
    source.buffer=buffer;source.loop=true
    filter.type='lowpass';filter.frequency.value=name==='water'?6000:1600;filter.Q.value=.4
    gain.gain.value=0
    source.connect(filter);filter.connect(gain);gain.connect(this.destination)
    const voice={source,gain,filter}
    source.onended=()=>{source.disconnect();filter.disconnect();gain.disconnect();this.retiring.delete(voice)}
    this.voices.set(name,voice)
    source.start(now)
    if(old) {
      old.gain.gain.cancelScheduledValues(now)
      old.gain.gain.setTargetAtTime(0,now,.15)
      this.retiring.add(old);old.source.stop(now+.8)
    }
    this.update(...this.state, fade ? .3 : .6)
  }

  update(throttle: number, load: number, speed: number, smoothing=.35): void {
    if(this.disposed) return
    this.state=[throttle,load,speed]
    const mix=boatAudioMix(this.kind,throttle,load,speed),now=this.context.currentTime
    for(const [name,voice] of this.voices) {
      const isWater=name==='water'
      voice.gain.gain.setTargetAtTime(isWater?mix.waterGain:name===mix.engine?mix.engineGain:0,now,smoothing)
      voice.source.playbackRate.setTargetAtTime(isWater?mix.waterRate:mix.rate,now,.55)
      voice.filter.frequency.setTargetAtTime(isWater?6000:mix.cutoff,now,.35)
    }
  }

  /** Quiet, rounded pulse/noise fallback, never the old continuous sawtooth buzz. */
  private backup(name: Layer): AudioBuffer {
    const rate=this.context.sampleRate, buffer=this.context.createBuffer(1,rate*2,rate)
    const data=buffer.getChannelData(0)
    let low=0,seed=9241
    for(let i=0;i<data.length;i++) {
      seed=(Math.imul(seed,1664525)+1013904223)>>>0
      low += ((seed/4294967296*2-1)-low)*.06
      const t=i/rate, pulse=Math.pow((Math.cos(2*Math.PI*(name==='diesel'?26:42)*t)+1)/2,8)
      data[i]=name==='water'?low*.35:Math.sin(2*Math.PI*78*t)*pulse*.18+low*.08
    }
    // Short seam fade in this emergency-only fallback avoids a repeating click.
    const edge=Math.floor(rate*.02)
    for(let i=0;i<edge;i++){data[i]*=i/edge;data[data.length-1-i]*=i/edge}
    return buffer
  }

  dispose(): void {
    if(this.disposed)return
    this.disposed=true
    for(const voice of [...this.voices.values(),...this.retiring]) {
      voice.source.onended=null
      voice.source.stop();voice.source.disconnect();voice.filter.disconnect();voice.gain.disconnect()
    }
    this.voices.clear();this.retiring.clear()
  }
}
